import { useSerializationUtils } from '@/composables/utils/useSerializationUtils.js'

export function useResource(baseUrl, options = {}) {
  const { queryStringFromParams } = useSerializationUtils()
  const baseHeaders = options.defaultHeaders || _defaultHeaders
  const baseOptions = options.defaultOptions || _defaultOptions
  const baseResourceOptions = options.defaultResourceOptions || _defaultResourceOptions
  let middlewares = options.middleware || []

  const ResponseType = {
    JSON: 'json',
    TEXT: 'text'
  }

  class ResourceError extends Error {
    constructor(message, response) {
      super(message)
      this.response = response
    }
  }

  function use(middleware) {
    middlewares.push(middleware)
  }

  async function perform(endpoint, method, body = null, headers = {}, allowedStatuses = null, resourceOptions = {}) {
    const requestUrl = _buildRequestUrl(endpoint)
    const requestResourceOptions = _buildRequestResourceOptions(resourceOptions)
    let requestOptions = _buildOptions(method, body, headers)
    requestOptions = _applyMiddleware(requestUrl, JSON.parse(JSON.stringify(requestOptions)), body)

    const response = await fetch(requestUrl, requestOptions)

    validateResponseStatus(response, allowedStatuses)
    return await _extractResponseData(response, requestResourceOptions)
  }

  async function get(endpoint, queryParameters = null, headers = {}, allowedStatuses = null, resourceOptions = {}) {
    if (queryParameters != null) {
      let serializedQueryParams = queryStringFromParams(queryParameters)
      endpoint = `${endpoint}?${serializedQueryParams}`
    }

    return await perform(endpoint, "GET", null, headers, allowedStatuses, resourceOptions)
  }

  async function post(endpoint, body = null, headers = {}, allowedStatuses = null, resourceOptions = {}) {
    return await perform(endpoint, "POST", body, headers, allowedStatuses, resourceOptions)
  }

  async function put(endpoint, body = null, headers = {}, allowedStatuses = null, resourceOptions = {}) {
    return await perform(endpoint, "PUT", body, headers, allowedStatuses, resourceOptions)
  }

  async function del(endpoint, body = null, headers = {}, allowedStatuses = null, resourceOptions = {}) {
    return await perform(endpoint, "DELETE", body, headers, allowedStatuses, resourceOptions)
  }

  function _buildOptions(method, body, headers) {
    const options = {
      ...baseOptions(),
      method: method,
      headers: { ...baseHeaders(), ...headers },
      body: JSON.stringify(body)
    }

    if (body == null) delete options.body

    return options
  }

  function _buildRequestUrl(endpoint) {
    let url = `${baseUrl}/${endpoint}`
    // Remove any additional `/` in the url. E.g https://my-api//another///path// => https://my-api/another/path/
    url = url.replace(/([^:]\/)\/+/g, "$1")
    return url
  }

  function _buildRequestResourceOptions(resourceOptions) {
    return { ...baseResourceOptions(), ...resourceOptions }
  }

  function _applyMiddleware(url, options, body) {
    for (let middleware of middlewares) {
      options = middleware(url, options, body)
    }

    return options
  }

  function validateResponseStatus(response, allowedStatuses) {
    if (allowedStatuses && !allowedStatuses.includes(response.status)) {
      throw new ResourceError(`Received invalid status '${response.status}'. Expected one of '${allowedStatuses.join(', ')}'`, response)
    }
  }

  async function _extractResponseData(response, requestResourceOptions) {
    const textBody = await response.text()
    let data = null

    if (textBody === "") {
      // An empty body still returns a readable stream. So we need to manually check if the body is empty
      data = {}
    }
    else {
      try {
        switch (requestResourceOptions.responseType) {
          case (ResponseType.JSON): {
            data = await JSON.parse(textBody)
            break
          }
          case (ResponseType.TEXT): {
            data = textBody
            break
          }
          default: {
            throw new Error()
          }
        }
      }
      catch(error) {
        throw new ResourceError(`Could not extract body from resource response. Expected a \`${requestResourceOptions.responseType}\` response type`, response)
      }
    }

    return data
  }

  function _defaultOptions() {
    return { }
  }

  function _defaultHeaders() {
    return { }
  }

  function _defaultResourceOptions() {
    return {
      responseType: ResponseType.JSON
    }
  }

  return {
    use,
    get,
    post,
    put,
    del,
    perform,
    ResourceError 
  }
}
